/**
 * SPA hook for the ActiveCrawlPanel on SearchPage.
 *
 * Wraps GET /api/keepa-schedules/active — returns enabled schedules + their
 * last-run summary + a tenant-wide capacity estimate against Keepa's
 * 60-tok/hr ceiling. The endpoint is tenant-scoped via the session cookie,
 * so the hook needs no params.
 *
 * Refetch cadence: every 30s while the page is in foreground. Matches the
 * design doc's "live-but-cheap" requirement — the panel needs to update as
 * the sweep makes progress without being aggressive enough to burn extra
 * cycles. The query is cheap (small DB read, no Keepa calls) so a tighter
 * interval is safe but unnecessary.
 *
 * See docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §1.
 */
import { useQuery } from '@tanstack/react-query';
import { ActiveSchedulesResponse } from '@sourcito/schemas';
import { apiFetch } from './api';

export const ACTIVE_SCHEDULES_KEY = ['active-schedules'] as const;

export function useActiveSchedules() {
  return useQuery({
    queryKey: ACTIVE_SCHEDULES_KEY,
    queryFn: () =>
      apiFetch<ActiveSchedulesResponse>('/api/keepa-schedules/active', {
        responseSchema: ActiveSchedulesResponse,
      }),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 25_000,
  });
}
